import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject, forkJoin } from 'rxjs';
import { RegistrationStep2Service } from '@pg-app/client/services/registrationStep2.service';
import { Router } from '@angular/router';
import { ClientProfileService } from '@pg-app/client/services/client-profile.service';
import Onfido from 'onfido-sdk-ui';
import { format } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { CheckAccountComponent } from '@pg-app/client/client-dashboard/dialogs/check-account/check-account.component';
import { MatDialog } from '@angular/material/dialog';
import { ShareService } from '@nttp/common-service/share-service';
import { Regex } from '@pg-app/shared/constants/regex';
import { CommonMessages } from '@pg-app/shared/constants/common-messages';
import { environment } from '@pg-env/environment';

export interface CommonModel {
  name: string;
  id: string;
}

export interface Currency {
  code: string;
  id: string;
  name: string;
}

export interface Country {
  code: string;
  id: string;
  code3: string;
  name: string;
}

export interface PostCode {
  id: string;
  next: string;
  text: string;
  description: string;
}

export interface FilesUploaded {
  originalFileName: string;
  uploadedPath: string;
}

@Component({
  selector: 'np-register-step2',
  templateUrl: './register-step2.component.html',
  styleUrls: ['./register-step2.component.scss'],

})

export class RegisterStep2Component implements OnInit {
  coporateForm: FormGroup;
  reasonLists: CommonModel[] = [];
  countries: any;
  destroy$ = new Subject<boolean>();
  showAddressList: boolean;
  showAddressDetail = false;
  addressResults: PostCode[];
  defaultAddress: PostCode[];
  hasAddressResult: boolean;
  completedOnfido: boolean;
  tokenOnfido: any;
  onfidoOut: any;
  today = new Date();
  minDOB: Date;
  notValidateAge = true;
  @Input()
  accountType: string;
  companyRegNo = '';
  companyName = '';
  loqateLabel = '';
  Address1 = '';
  validateDOB = false;
  agree = false;
  accountInfo = null;

  // Uploading app supporting docs
  completedUploadingSupportingDocs: boolean;
  allowAutoAddressApproval: boolean;

  targetFile: File = null;
  private validFileExtensions = ['.gif', '.jpg', '.png', '.pdf', '.tiff'];
  filesUploaded: FilesUploaded[] = [];
  maxFilesUploaded = 6;

  commonMessage = CommonMessages;

  isPrivalgoMarkets = environment.isPrivalgoMarkets;
  domain = environment.domain;
  brandName = environment.brandName;
  brandNameUpperCase = this.brandName.toUpperCase();

  get flatnoCtrl(): FormControl {
    return this.coporateForm.get('flatno') as FormControl;
  }

  get buildingnameCtrl(): FormControl {
    return this.coporateForm.get('buildingname') as FormControl;
  }

  get buildingnumCtrl(): FormControl {
    return this.coporateForm.get('buildingnum') as FormControl;
  }

  get streetCtrl(): FormControl {
    return this.coporateForm.get('street') as FormControl;
  }

  get cityCtrl(): FormControl {
    return this.coporateForm.get('city') as FormControl;
  }

  get postCodeCtrl(): FormControl {
    return this.coporateForm.get('postCode') as FormControl;
  }

  get countryCtrl(): FormControl {
    return this.coporateForm.get('country') as FormControl;
  }

  get countyStateCtrl(): FormControl {
    return this.coporateForm.get('countyState') as FormControl;
  }

  get addressCtrl(): FormControl {
    return this.coporateForm.get('address') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    private registrationStep2Service: RegistrationStep2Service,
    private clientProfileService: ClientProfileService,
    private toastr: ToastrService,
    private router: Router,
    private dialog: MatDialog,
    private shareService: ShareService,
  ) {
  }

  ngOnInit() {
    this.completedOnfido = false;
    this.completedUploadingSupportingDocs = false;
    this.coporateForm = this.fb.group({
      idVerify: '',
      day: [''],
      month: [''],
      year: [''],
      address: '',

      flatno: ['', [Validators.pattern(Regex.safeCharacters)]],
      buildingname: ['', [Validators.pattern(Regex.safeCharacters)]],
      buildingnum: ['', [Validators.pattern(Regex.safeCharacters)]],
      street: ['', [Validators.required, Validators.pattern(Regex.safeCharacters)]],
      city: ['', [Validators.required, Validators.pattern(Regex.safeCharacters)]],
      postCode: ['', [Validators.required, Validators.pattern(Regex.safeCharacters)]],
      countyState: ['', [Validators.pattern(Regex.safeCharacters)]],
      country: ['', Validators.required],
    });

    if (this.accountType !== 'client') {
      this.coporateForm.get('street').clearValidators();
      this.coporateForm.updateValueAndValidity();
    }

    this.minDOB = new Date(this.today.getFullYear() - 16, this.today.getMonth(), this.today.getDate());
    const getCountry = this.shareService.countryList$;
    const accountInfo = this.registrationStep2Service.getUserInfo();
    forkJoin([getCountry, accountInfo]).subscribe(res => {
      this.getAllData(res);
    });
  }

  onSubmit() {
    this.coporateForm.markAllAsTouched();
    if (!this.coporateForm.valid || this.notValidateAge || !this.validateDOB) {
      return;
    }

    if (this.accountType === 'client' && !this.buildingnameCtrl.value && !this.buildingnumCtrl.value) {
      return;
    }

    if (!this.agree) {
      this.toastr.error('Please confirm acceptance of the terms and conditions to proceed.');
      return;
    }

    if (this.accountType === 'client' && !this.allowAutoAddressApproval) {
      // Show final dialogues for private client registration
      this.dialog.open(CheckAccountComponent, {
        disableClose: true,
        data: {
          hasButton: true,
          message: 'Please note that we may require an explanation and documentation that shows your source of wealth. '
            + 'This will depend on the nature and size of the transaction.',
          buttonText: 'Continue'
        },
        width: '40vw'
      })
        .afterClosed().subscribe(() => {
          this.dialog.open(CheckAccountComponent, {
            disableClose: true,
            data: {
              hasButton: true,
              message: `Thank you for registering to open an account with ${this.brandName}. `
                + 'It is our privilege to work alongside you and we will be in touch shortly to welcome you onboard.'
            },
            width: '40vw'
          })
            .afterClosed().subscribe(() => {
              this.addCorporate();
            });
        });
    } else {
      this.addCorporate();
    }
  }

  private addCorporate() {
    const routing = this.accountType === 'partner' ? 'partner/dashboard' : 'client/dashboard';
    const selectedCountry = this.coporateForm.get('country').value;

    const corporate = {
      dateOfBirth: `${this.coporateForm.get('year').value}${this.coporateForm.get('month').value}${this.coporateForm.get('day').value}`,
      address1: this.formatAddress1(),
      postCode: this.postCodeCtrl.value,
      city: this.cityCtrl.value,
      country: selectedCountry ? selectedCountry : null,
      isStep2Registered: true,
      stateProvince: this.countyStateCtrl.value
    };

    this.registrationStep2Service.addCorporate(corporate).subscribe(
      data => {
        this.toastr.success('Registered successfully!');
        this.router.navigate([`${routing}`]);
        setTimeout(() => {
          this.showDialog();
        }, 100);
        this.callCheckAfterUpload(this.tokenOnfido.applicantId);
      },
      err => {
        window.scrollTo(0, 0);
      }
    );
  }

  getAllData(data) {
    this.countries = data[0];
    this.accountInfo = data[1];
    this.getUserInfo(data[1]);
  }

  searchPostCode() {
    const postCode = {
      text: this.postCodeCtrl.value,
      lastId: ''
    };
    if (this.addressCtrl != null) {
      this.addressCtrl.setValue('');
    }
    this.clientProfileService.getPostCode(postCode).subscribe(
      res => {
        this.showAddressList = true;
        this.coporateForm.get('address').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
          this.filterAddress(value);
        });
        this.defaultAddress = res;
        this.addressResults = res;

        if (res.length === 0) {
          this.addressCtrl.setValue('No result found');
        }
      },
    );
  }

  chooseAddress(addressSelected: PostCode) {
    const postCodeText = {
      text: addressSelected.text,
      lastId: addressSelected.id
    };
    const postCodeId = {
      lastId: addressSelected.id
    };
    this.addressCtrl.setValue(addressSelected.text);
    if (addressSelected.next === 'Find') {
      this.clientProfileService.getPostCode(postCodeText).subscribe(
        res => {
          this.addressResults = res;
          if (res.length > 0) {
            this.showAddressList = true;
          }
          this.defaultAddress = res;
        },
      );
    } else if (addressSelected.next === 'Retrieve') {
      this.showAddressList = false;
      this.showAddressDetail = true;
      this.clientProfileService.getAddressDetails(postCodeId).subscribe(
        res => {
          this.postCodeCtrl.setValue(res.postalCode);
          this.loqateLabel = res.label;
          this.flatnoCtrl.setValue(res.subBuilding);
          this.buildingnameCtrl.setValue(res.buildingName);
          this.buildingnumCtrl.setValue(res.buildingNumber);
          this.streetCtrl.setValue(res.street);
          this.cityCtrl.setValue(res.city);
          this.countyStateCtrl.setValue(res.provinceName);
          const country = this.countries.find(d => d.code === res.countryIso2);
          this.countryCtrl.setValue(country);
        },
      );
    }
  }

  enterManually() {
    this.showAddressDetail = true;
    this.showAddressList = false;
    this.flatnoCtrl.setValue('');
    this.buildingnameCtrl.setValue('');
    this.buildingnumCtrl.setValue('');
    this.streetCtrl.setValue('');
    this.cityCtrl.setValue('');
    this.countyStateCtrl.setValue('');
    this.countryCtrl.setValue('');
  }

  onDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  filterAddress(address: string) {
    if (address === '') {
      this.addressResults = this.defaultAddress;
    } else {
      this.addressResults = this.defaultAddress.filter(d => d.text.toLowerCase().includes(address));
    }
  }

  prepareDataToGenerateToken() {
    const nowTime = format(new Date());
    const data = {
      id: '',
      created_at: nowTime,
      sandbox: false,
      title: null,
      first_name: this.accountInfo.firstName ? this.accountInfo.firstName : '',
      middle_name: this.accountInfo.middleName ? this.accountInfo.middleName : '',
      last_name: this.accountInfo.lastName ? this.accountInfo.lastName : '',
      email: this.accountInfo.email ? this.accountInfo.email : '',
      gender: 'female',
      dob: this.coporateForm.get('year').value && this.coporateForm.get('month').value && this.coporateForm.get('day').value ?
        `${this.coporateForm.get('year').value}-${this.coporateForm.get('month').value}-${this.coporateForm.get('day').value}` : null,
      telephone: this.accountInfo.phoneNumber ? this.accountInfo.phoneNumber : '',
      mobile: null,
      nationality: null,
      country: this.countryCtrl.value.code3 ? this.countryCtrl.value.code3 : 'GBR',
      href: '',
      mothers_maiden_name: null,
      country_of_birth: null,
      town_of_birth: null,
      previous_last_name: null,
      id_numbers: [],
      addresses: []
    };
    return data;
  }

  checkNullValueAndReturnComma(value) {
    if (value !== null && value !== undefined && value !== '') {
      return ', ';
    }
    return '';
  }

  formatAddress1() {
    let part1 = '';
    if (this.flatnoCtrl.value !== null && this.flatnoCtrl.value !== undefined && this.flatnoCtrl.value !== '') {
      part1 = this.flatnoCtrl.value + ' ';
    }
    if (
      this.buildingnameCtrl.value !== null &&
      this.buildingnameCtrl.value !== undefined &&
      this.buildingnameCtrl.value !== ''
    ) {
      part1 = part1 + this.buildingnameCtrl.value;
    }

    let part2 = '';
    if (
      this.buildingnumCtrl.value !== null &&
      this.buildingnumCtrl.value !== undefined &&
      this.buildingnumCtrl.value !== ''
    ) {
      part2 = this.buildingnumCtrl.value + ' ';
    }
    if (this.streetCtrl.value !== null && this.streetCtrl.value !== undefined && this.streetCtrl.value !== '') {
      part2 = part2 + this.streetCtrl.value;
    }

    let Address1 = part1;
    if (Address1 !== '') {
      Address1 = Address1 + this.checkNullValueAndReturnComma(part2) + part2;
    } else {
      Address1 = part2;
    }
    return Address1;
  }

  callCheckAfterUpload(obj) {
    this.loqateLabel =
      this.formatAddress1() +
      this.checkNullValueAndReturnComma(this.cityCtrl.value) +
      this.cityCtrl.value +
      this.checkNullValueAndReturnComma(this.countyStateCtrl.value) +
      this.countyStateCtrl.value +
      this.checkNullValueAndReturnComma(this.postCodeCtrl.value) +
      this.postCodeCtrl.value +
      this.checkNullValueAndReturnComma(this.countryCtrl.value) +
      this.countryCtrl.value.name;
    const arrName = [].concat(this.accountInfo.firstName, this.accountInfo.middleName, this.accountInfo.lastName);
    const data = {
      CompanyRegNo: null,
      CountryCode3: this.countryCtrl.value.code3 ? this.countryCtrl.value.code3 : 'GBR',
      Name: arrName.filter(x => !!x).join(' '),
      ApplicantId: obj,
      Dob: this.coporateForm.get('year').value && this.coporateForm.get('month').value && this.coporateForm.get('day').value ?
        `${this.coporateForm.get('year').value}-${this.coporateForm.get('month').value}-${this.coporateForm.get('day').value}` : null,
      PostalCode: this.postCodeCtrl.value,
      Address: this.loqateLabel,
      AddressItemized: {
        FlatNo: this.flatnoCtrl.value,
        BuildingName: this.buildingnameCtrl.value,
        BuildingNumber: this.buildingnumCtrl.value,
        Street: this.streetCtrl.value,
        City: this.cityCtrl.value,
        CountyState: this.countyStateCtrl.value,
        PostCode: this.postCodeCtrl.value,
        Country: this.countryCtrl.value.Name
      }
    };

    // call service
    this.registrationStep2Service.checkDocumentOnfido(data).subscribe(
      checkRes => {

        // Uploading supporting documents to Passfort.
        // This is an async process so no need to give any user feedback on UI
        this.registrationStep2Service.uploadSupportingDocs(this.filesUploaded).subscribe(
          uploadOk => {
          },
        );
      });
  }

  generateSDKToken(obj) {
    this.registrationStep2Service.generateSDKToken(obj).subscribe(
      data => {
        this.tokenOnfido = data;
        this.initOnfido();
      },
    );
  }

  initOnfido() {
    const _self = this;

    if (this.onfidoOut !== undefined) {
      this.onfidoOut.tearDown();
    }
    this.onfidoOut = Onfido.init({
      token: this.tokenOnfido.sdkToken,
      containerId: 'onfido-mount',
      onComplete: function (data) {
        _self.completedOnfido = true;
        _self.allowAutoAddressApproval = _self.accountType === 'client' ?
          _self.countryAllowsAutoAddressApproval() :
          true;
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      },
      steps: ['document', 'face', 'complete']
    });
  }

  uploadOnfido() {
    this.coporateForm.markAllAsTouched();
    if (!this.coporateForm.valid || this.notValidateAge || !this.validateDOB) {
      return;
    }

    if (this.accountType === 'client' && !this.buildingnameCtrl.value && !this.buildingnumCtrl.value) {
      return;
    }

    const data = this.prepareDataToGenerateToken();
    this.generateSDKToken(data);
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  }

  getUserInfo(data) {
    if (data.dateOfBirth) {
      this.coporateForm.get('day').setValue(this.convert(new Date(data.dateOfBirth).getDate(), 2));
      this.coporateForm.get('month').setValue(this.convert((new Date(data.dateOfBirth).getMonth() + 1), 2));
      this.coporateForm.get('year').setValue(this.convert(new Date(data.dateOfBirth).getFullYear(), 4));
    }
    if (data.postCode) {
      this.showAddressDetail = true;
      this.postCodeCtrl.setValue(data.postCode);
      this.countyStateCtrl.setValue(data.city);
      const country = this.countries.find(item => item.id === data.country.id);
      this.countryCtrl.setValue(country);
    }

    if (data.organizationInfo) {
      this.companyRegNo = data.organizationInfo.companyRegNo;
      this.companyName = data.organizationInfo.businessName;
    }
  }

  changeChecked() {
    this.agree = !this.agree;
  }

  togglePicker(picker) {
    picker.opened ? picker.close() : picker.open();
  }

  convert(rawValue, length) {
    const convertValue = length === 2 ? '00' + rawValue.toString() : '0000' + rawValue.toString();
    return convertValue.slice(length * -1);
  }

  isLeapYear(year) {
    return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
  }

  getDaysInMonth(year, month) {
    const dayInMonth = [31, this.isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return dayInMonth[month];
  }

  checkValidateDOB(controlName) {
    if (this.coporateForm.get(controlName).value) {
      const length = controlName === 'year' ? 4 : 2;
      this.coporateForm.get(controlName).setValue(this.convert(this.coporateForm.get(controlName).value, length));
    }
    const day = this.coporateForm.get('day').value;
    const month = this.coporateForm.get('month').value;
    const year = this.coporateForm.get('year').value;
    if (
      Number(month) === 0 || Number(year) === 0 || Number(day) === 0 ||
      parseFloat(month) > 12 || parseFloat(day) > this.getDaysInMonth(year, parseFloat(month) - 1)
    ) {
      this.validateDOB = false;
      return;
    }

    this.validateDOB = true;
    this.notValidateAge = new Date(year, month - 1, day) > this.minDOB ? true : false;
  }

  showDialog() {
    const message =
      'We are currently processing your account information.<br> You will be notified by email when the registration process is complete. <br> Click OK to refresh.';
    const dialogRef = this.dialog.open(CheckAccountComponent, {
      disableClose: true,
      data: {
        hasButton: true,
        message: message
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      window.location.reload();
    });
  }

  public uploadFileChanged(event) {
    if (this.filesUploaded.length >= this.maxFilesUploaded) {
      this.toastr.warning('You have already uploaded the maximum number of files allowed');
      return;
    }
    const files = [].concat(...event.addedFiles);
    let file: File = null;
    if (files.length > 0) {
      file = files[0];

      const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      const fileSize = file.size;

      if (this.validFileExtensions.indexOf(fileExt) < 0) {
        // Checking for valid file extension
        this.toastr.warning('Invalid file extension');

      } else if (fileSize > 10000000) {
        // Check for file size. Max size is 10MB
        this.toastr.warning('File size cannot exceed 10MB');

      } else {
        this.targetFile = file;
      }
    }
  }

  public saveSupportFile() {
    const file = this.targetFile;
    this.registrationStep2Service.saveSupportingDoc(file).subscribe(
      data => {
        if (data.uploadedOk) {
          this.toastr.success('Successfully uploaded file ' + data.originalFileName);
          this.filesUploaded.push(data);

          setTimeout(() => {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: 'smooth'
            });
          }, 100);
        } else {
          this.toastr.error('Cannot upload the file. ' + data.message);
        }
        this.targetFile = null;
      },
      error => {
        this.toastr.error('Cannot upload the file');
        this.targetFile = null;
      }
    );
  }

  public getValidFileExtensionsString() {
    let result = '';
    this.validFileExtensions.forEach(x => {
      result += (result !== '' ? ',' : '') + x;
    });
    return result;
  }

  private countryAllowsAutoAddressApproval() {
    const country = this.countryCtrl.value;
    return (country !== undefined && country !== null) ? country.autoAddressApproval : false;
  }

  public finishedUploadingClicked() {
    this.completedUploadingSupportingDocs = true;

    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  }

  onRemove() {
    this.targetFile = null;
  }

}
